import React, { useState, useEffect } from "react";
import AdminLayout from "../../components/layout/AdminLayout";
import { Button, InputField } from "../../components/common";
import { StatusBadge, SkeletonLoader, EmptyState } from "../../components/admin";
import { useToast } from "../../contexts/ToastContext";
import adminService, {
  AdminApplication,
} from "../../services/api/adminService";

interface DocumentInfo {
  id: string;
  name: string;
  documentType: string;
  isVerified: boolean;
  uploadedAt: string;
  applicationId: string;
  applicationNumber: string;
  studentName: string;
  studentEmail: string;
  courseName: string;
  universityName: string;
}

const DocumentManagement: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [verifyingDocs, setVerifyingDocs] = useState<Set<string>>(new Set());

  const { showError, showSuccess } = useToast();

  const documentTypes = [
    "PASSPORT_PHOTO",
    "TRANSCRIPT",
    "IELTS_CERTIFICATE",
    "DEGREE_CERTIFICATE",
    "RECOMMENDATION_LETTER",
    "PERSONAL_STATEMENT",
    "CV_RESUME",
    "FINANCIAL_DOCUMENTS",
    "OTHER",
  ];

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedType, selectedStatus]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await adminService.getApplications({ limit: 1000 });
      const allDocuments: DocumentInfo[] = [];
      response.data.applications.forEach((app: AdminApplication) => {
        app.documents.forEach((doc) => {
          allDocuments.push({
            id: doc.id,
            name: doc.documentType,
            documentType: doc.documentType,
            isVerified: doc.isVerified,
            uploadedAt: doc.uploadedAt,
            applicationId: app.id,
            applicationNumber: app.applicationNumber,
            studentName: `${app.givenNames} ${app.familyName}`,
            studentEmail: app.email,
            courseName: app.course.title,
            universityName: app.course.university.name,
          });
        });
      });

      let filteredDocs = allDocuments;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredDocs = filteredDocs.filter(
          (doc) =>
            doc.studentName.toLowerCase().includes(term) ||
            doc.studentEmail.toLowerCase().includes(term) ||
            doc.applicationNumber.toLowerCase().includes(term) ||
            doc.courseName.toLowerCase().includes(term)
        );
      }
      if (selectedType) {
        filteredDocs = filteredDocs.filter((doc) => doc.documentType === selectedType);
      }
      if (selectedStatus) {
        const isVerified = selectedStatus === "verified";
        filteredDocs = filteredDocs.filter((doc) => doc.isVerified === isVerified);
      }
      setDocuments(filteredDocs);
    } catch (err: any) {
      setError(err.message || "Failed to fetch documents");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDocument = async (documentId: string, isVerified: boolean) => {
    try {
      setVerifyingDocs((prev) => new Set(prev).add(documentId));
      await adminService.verifyDocument(documentId, {
        verificationStatus: isVerified ? "VERIFIED" : "REJECTED",
        isVerified,
      });
      showSuccess(isVerified ? "Document verified successfully" : "Document rejected successfully");
      fetchDocuments();
    } catch (err: any) {
      showError(err.message || "Failed to update document status");
    } finally {
      setVerifyingDocs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const verifiedCount = documents.filter((doc) => doc.isVerified).length;
  const pendingCount = documents.filter((doc) => !doc.isVerified).length;
  const verificationRate = documents.length > 0
    ? Math.round((verifiedCount / documents.length) * 100)
    : 0;

  const activeFilters = [
    selectedType && { label: selectedType.replace(/_/g, " "), clear: () => setSelectedType("") },
    selectedStatus && { label: selectedStatus === "verified" ? "Verified" : "Pending", clear: () => setSelectedStatus("") },
    searchTerm && { label: `"${searchTerm}"`, clear: () => setSearchTerm("") },
  ].filter(Boolean) as { label: string; clear: () => void }[];

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
          <p className="text-sm text-gray-500 mt-1">
            Review and verify student documents
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total", value: documents.length, color: "text-gray-900" },
            { label: "Verified", value: verifiedCount, color: "text-emerald-600" },
            { label: "Pending", value: pendingCount, color: "text-amber-600" },
            { label: "Rate", value: `${verificationRate}%`, color: "text-blue-600" },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-center">
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <InputField
              name="search"
              type="text"
              label="Search"
              placeholder="Student name, email, or application..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-shadow"
              >
                <option value="">All Types</option>
                {documentTypes.map((type) => (
                  <option key={type} value={type}>{type.replace(/_/g, " ")}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-shadow"
              >
                <option value="">All Status</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={fetchDocuments} className="w-full" disabled={loading}>
                {loading ? "Loading..." : "Refresh"}
              </Button>
            </div>
          </div>

          {activeFilters.length > 0 && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">Filters:</span>
              {activeFilters.map((f, i) => (
                <button
                  key={i}
                  onClick={f.clear}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-cyan-50 text-cyan-700 rounded-full hover:bg-cyan-100 transition-colors"
                >
                  {f.label}
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Document</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Student</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Application</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Course</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Status</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Uploaded</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonLoader variant="table-row" count={5} columns={7} />
                ) : (
                  documents.map((document) => (
                    <tr key={document.id} className="border-b border-gray-50 hover:bg-cyan-50/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-gray-900">
                          {document.documentType.replace(/_/g, " ")}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-gray-900">{document.studentName}</p>
                        <p className="text-xs text-gray-400">{document.studentEmail}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-gray-900">{document.applicationNumber}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-gray-900">{document.courseName}</p>
                        <p className="text-xs text-gray-400">{document.universityName}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge
                          status={document.isVerified ? "VERIFIED" : "PENDING"}
                          type="email"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(document.uploadedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {!document.isVerified ? (
                            <>
                              <button
                                onClick={() => handleVerifyDocument(document.id, true)}
                                disabled={verifyingDocs.has(document.id)}
                                className="px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-md hover:bg-emerald-100 transition-colors disabled:opacity-50"
                              >
                                {verifyingDocs.has(document.id) ? "..." : "Verify"}
                              </button>
                              <button
                                onClick={() => handleVerifyDocument(document.id, false)}
                                disabled={verifyingDocs.has(document.id)}
                                className="px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50"
                              >
                                {verifyingDocs.has(document.id) ? "..." : "Reject"}
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleVerifyDocument(document.id, false)}
                              disabled={verifyingDocs.has(document.id)}
                              className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                            >
                              {verifyingDocs.has(document.id) ? "..." : "Unverify"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty State */}
        {documents.length === 0 && !loading && (
          <EmptyState
            title="No documents found"
            description="Try adjusting your search criteria or refresh the page."
            actionLabel="Clear Filters"
            onAction={() => { setSearchTerm(""); setSelectedType(""); setSelectedStatus(""); }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default DocumentManagement;
